import {
  resolveEntityPhotoUrl,
  entityPhotoHasImage,
  activeDomainImage,
} from '../../../../utils/imageProxy';
import { ensureSignedProxyUrls } from '../../../../utils/signedImageProxyCache';
import { setMachineDetailsData } from '../../../../redux/machineDetailsDataSlice';
import machineDefaultImage from '../../../../images/machineImageEmpty.svg';
import { CustomErrorLogger } from '../../../../utils/CustomErrorLogger';
import { useHasPermission } from '../../../../hooks/useHasPermission';
import { EntityImageEditor } from '../../../entityImage/EntityImageEditor';
import EntityPhotoFrame from '../../../entityImage/EntityPhotoFrame';
import { deleteEntityImage } from '../../../entityImage/entityImageApi';
import { syncListEntityImage } from '../../../entityImage/entityImageListSync';
import { getEntityImageConfig } from '../../../entityImage/entityImageConfig';
import OpenImageDialog from '../../../openImageDialog';
import { useSelector, useDispatch } from 'react-redux';
import { DeleteRecord } from '../../../deleteRecord';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useCallback, useMemo } from 'react';
import Iconify from '../../../Iconify';
import '../../../../css/machines/machineImageActions.css';

const MachineImageActions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const config = useMemo(() => getEntityImageConfig('machine'), []);
  const machineDetailsData = useSelector(config.detailsSelector);
  const hasMachineUpdatePermission = useHasPermission(config.permission);
  const canMutate =
    hasMachineUpdatePermission && !machineDetailsData?.is_archived;

  const [openImage, setOpenImage] = useState(false);
  const [deleteImage, setDeleteImage] = useState(false);
  const [modifyPhoto, setModifyPhoto] = useState(false);

  const photoOpts = useMemo(
    () => ({ imageField: config.imageField }),
    [config.imageField],
  );

  const displayUrl = resolveEntityPhotoUrl(machineDetailsData, {
    ...photoOpts,
    size: 'medium',
    fallback: machineDefaultImage,
  });
  const fullUrl = resolveEntityPhotoUrl(machineDetailsData, {
    ...photoOpts,
    size: 'default',
    fallback: '',
  });
  const hasImage = entityPhotoHasImage(machineDetailsData, {
    ...photoOpts,
    size: 'medium',
  });

  const openImageEditor = useCallback(() => {
    const activeImage = activeDomainImage(machineDetailsData, config.imageField);
    if (activeImage?.key_source) {
      ensureSignedProxyUrls([activeImage.key_source], { silent: true });
    }
    setModifyPhoto(true);
  }, [config.imageField, machineDetailsData]);

  const handleDeleteImage = useCallback(async () => {
    try {
      if (!machineDetailsData?.id) {
        throw new Error('Aucun identifiant disponible.');
      }
      const response = await deleteEntityImage(config, machineDetailsData.id);
      if (response.status === 200 || response.status === 201) {
        setDeleteImage(false);
        dispatch(
          setMachineDetailsData({
            ...machineDetailsData,
            [config.imageField]: [],
          }),
        );
        syncListEntityImage({
          entityId: machineDetailsData.id,
          imageField: config.imageField,
          image: null,
        });
      }
    } catch (error) {
      console.error(t("Erreur lors de la suppression de l'image de la machine:"), error);
      CustomErrorLogger(error, 'MachineImageActions.delete');
    }
  }, [config, dispatch, machineDetailsData, t]);

  const activeImage = activeDomainImage(machineDetailsData, config.imageField);

  return (
    <>
    <EntityPhotoFrame
      image={activeImage}
      src={displayUrl}
      alt={config.alt}
      className="machineImageActions_image_icon_box"
      showBlur={hasImage}
    >
      {hasImage ? (
        <Box>
          {canMutate && (
            <Box className="machineImageActions_edit_delete_icon_box">
              <Iconify
                className="machineImageActions_edit_icon"
                icon="ic:outline-edit"
                onClick={openImageEditor}
              />
              <Iconify
                className="machineImageActions_delete_icon"
                icon="ic:outline-delete"
                onClick={() => setDeleteImage(true)}
              />
            </Box>
          )}
          <Iconify
            className="machineImageActions_maximize_icon"
            icon="tabler:arrows-maximize"
            onClick={() => setOpenImage(true)}
          />
        </Box>
      ) : (
        canMutate && (
          <Iconify
            className="machineImageActions_plus_icon"
            icon="ic:baseline-plus"
            onClick={openImageEditor}
          />
        )
      )}
    </EntityPhotoFrame>
      {modifyPhoto && (
        <EntityImageEditor
          entityType="machine"
          entityId={machineDetailsData?.id}
          entityData={machineDetailsData}
          modifyPhoto={modifyPhoto}
          setModifyPhoto={setModifyPhoto}
        />
      )}

      {openImage && (
        <OpenImageDialog
          profileUrl={fullUrl}
          openImage={openImage}
          setOpenImage={setOpenImage}
        />
      )}

      {deleteImage && (
        <DeleteRecord
          openDelete={deleteImage}
          setOpenDelete={setDeleteImage}
          handleDelete={handleDeleteImage}
          title={t(config.deleteTitle)}
        />
      )}
    </>
  );
};

export default MachineImageActions;
